import React, { useState, useEffect } from 'react';
import axiosInstance from '@/axios';
import Modal from '@/pages/Other/Modal';
import { tr } from 'date-fns/locale';

const About = () => {
    // About Section State
    const [aboutInfo, setAboutInfo] = useState(null);
    // About Stats State
    const [aboutStats, setAboutStats] = useState([]);

    // Loading states
    const [loading, setLoading] = useState(false);
    const [loadingStats, setLoadingStats] = useState(false);

    // Modal controls
    const [showSectionModal, setShowSectionModal] = useState(false);
    const [sectionModalType, setSectionModalType] = useState('create'); // 'create' or 'edit'

    const [showStatModal, setShowStatModal] = useState(false);
    const [statModalType, setStatModalType] = useState('create'); // 'create' or 'edit'


    const [uploadingFile, setUploadingFile] = useState(null); // file selected for upload
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadField, setUploadField] = useState(null); // 'videoUrl' or 'videoThumbnail' - which field user is uploading for

    // Form data for About Section
    const [sectionFormData, setSectionFormData] = useState({
        id: '',
        title: '',
        mainDescription: '',
        detailedDescription: '',
        videoUrl: '',
        videoThumbnail: '',
    });

    // Form data for About Stat
    const [statFormData, setStatFormData] = useState({
        id: '',
        number: '',
        label: '',
        backgroundColor: '#ffffff',
        borderColor: '#000000',
    });

    // Fetch About Section and Stats on mount
    useEffect(() => {
        fetchAboutInfo();
        fetchAboutStats();
    }, []);

    // Fetch About Section (assuming only one or the first)
    const fetchAboutInfo = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/public/about/section');
            if (res.data.success && res.data.data) {
                // If multiple sections, take the first one
                const section = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
                setAboutInfo(section);
            } else {
                setAboutInfo(null);
            }
        } catch (error) {
            console.error('Failed to fetch About info:', error);
            setAboutInfo(null);
        } finally {
            setLoading(false);
        }
    };

    // Fetch About Stats
    const fetchAboutStats = async () => {
        try {
            setLoadingStats(true);
            const res = await axiosInstance.get('/public/about/stats');
            if (res.data.success && Array.isArray(res.data.data)) {
                setAboutStats(res.data.data);
            } else {
                setAboutStats([]);
            }
        } catch (error) {
            console.error('Failed to fetch About stats:', error);
            setAboutStats([]);
        } finally {
            setLoadingStats(false);
        }
    };

    // Open About Section modal to create
    const openCreateSectionModal = () => {
        setSectionFormData({
            id: '',
            title: '',
            mainDescription: '',
            detailedDescription: '',
            videoUrl: '',
            videoThumbnail: '',
        });
        setSectionModalType('create');
        setShowSectionModal(true);
    };

    // Open About Section modal to edit
    const openEditSectionModal = () => {
        if (!aboutInfo) return;
        setSectionFormData({ ...aboutInfo });
        setSectionModalType('edit');
        setShowSectionModal(true);
    };

    // Open About Stat modal to create
    const openCreateStatModal = () => {
        setStatFormData({
            id: '',
            number: '',
            label: '',
            backgroundColor: '#ffffff',
            borderColor: '#000000',
        });
        setStatModalType('create');
        setShowStatModal(true);
    };

    // Open About Stat modal to edit
    const openEditStatModal = (stat) => {
        setStatFormData({
            id: stat.id || '',
            number: stat.number || '',
            label: stat.label || '',
            backgroundColor: stat.backgroundColor || '#ffffff',
            borderColor: stat.borderColor || '#000000',
        });
        setStatModalType('edit');
        setShowStatModal(true);
    };

    // Handle Section form input change
    const handleSectionInputChange = (e) => {
        const { name, value } = e.target;
        console.log(`Section Input Change - ${name}: ${value}`);
        setSectionFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handle Stat form input change
    const handleStatInputChange = (e) => {
        const { name, value } = e.target;
        setStatFormData(prev => ({ ...prev, [name]: value }));
    };

    // Submit Section create or update
    const handleSectionSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (sectionModalType === 'create') {
                await axiosInstance.post('/public/about/section', sectionFormData);
            } else {
                await axiosInstance.put(`/public/about/section/${sectionFormData.id}`, sectionFormData);
            }
            await fetchAboutInfo();
            setShowSectionModal(false);
        } catch (error) {
            console.error('Error saving About Section:', error);
            alert('Failed to save About Section.');
        } finally {
            setLoading(false);
        }
    };

    // Submit Stat create or update
    const handleStatSubmit = async (e) => {
        e.preventDefault();
        setLoadingStats(true);
        try {
            if (statModalType === 'create') {
                await axiosInstance.post('/public/about/stats', statFormData);
            } else {
                await axiosInstance.put(`/public/about/stats/${statFormData.id}`, statFormData);
            }
            await fetchAboutStats();
            setShowStatModal(false);
        } catch (error) {
            console.error('Error saving About Stat:', error);
            alert('Failed to save About Stat.');
        } finally {
            setLoadingStats(false);
        }
    };

    // Delete About Section
    const handleDeleteSection = async () => {
        if (!aboutInfo) return;
        if (!window.confirm('Are you sure you want to delete the About Section?')) return;

        setLoading(true);
        try {
            await axiosInstance.delete(`/public/about/section/${aboutInfo.id}`);
            setAboutInfo(null);
        } catch (error) {
            console.error('Error deleting About Section:', error);
            alert('Failed to delete About Section.');
        } finally {
            setLoading(false);
        }
    };

    // Delete About Stat
    const handleDeleteStat = async (id) => {
        if (!window.confirm('Are you sure you want to delete this About Stat?')) return;

        setLoadingStats(true);
        try {
            await axiosInstance.delete(`/public/about/stats/${id}`);
            await fetchAboutStats();
        } catch (error) {
            console.error('Error deleting About Stat:', error);
            alert('Failed to delete About Stat.');
        } finally {
            setLoadingStats(false);
        }
    };
    const handleFileSelect = (e, fieldName) => {
        const file = e.target.files[0];
        if (file) {
            setUploadingFile(file);
            setUploadField(fieldName);
        }
    };
    const handleUploadFile = async () => {
        if (!uploadingFile || !uploadField) return;

        setUploadLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadingFile);

            const res = await axiosInstance.post('/image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (res.data && res.data.url) {
                // Set the returned URL into the correct form field
                setSectionFormData(prev => ({
                    ...prev,
                    [uploadField]: res.data.url,
                }));

                // Clear upload state
                setUploadingFile(null);
                setUploadField(null);
            } else {
                alert('Upload failed: no URL returned');
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload file');
        } finally {
            setUploadLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-5 mt-5">
            <h1 className="text-3xl font-bold mb-6">About Section Management</h1>

            {loading && <div className="mb-4 text-indigo-600 font-semibold">Loading About Section...</div>}

            {/* About Section display */}
            {aboutInfo ? (
                <div className="mb-8 bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                    {/* Header with gradient background */}
                    <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-4">
                        <h1 className="text-2xl font-bold text-white">{aboutInfo.title}</h1>
                    </div>

                    <div className="p-6 space-y-8">
                        {/* Main Description Card */}
                        <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-indigo-500">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                                <h2 className="text-lg font-semibold text-gray-800">Overview</h2>
                            </div>
                            <p className="text-gray-700 leading-relaxed">{aboutInfo.mainDescription}</p>
                        </div>

                        {/* Detailed Description Card */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 border border-blue-100">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <h2 className="text-lg font-semibold text-gray-800">Details</h2>
                            </div>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{aboutInfo.detailedDescription}</p>
                        </div>

                        {/* Video Section */}
                        {aboutInfo.videoUrl && (
                            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-5 border border-purple-100">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                    <h2 className="text-lg font-semibold text-gray-800">Featured Video</h2>
                                </div>
                                <div className="flex justify-center">
                                    <video
                                        controls
                                        src={aboutInfo.videoUrl}
                                        poster={aboutInfo.videoThumbnail}
                                        className="w-full max-w-2xl rounded-lg shadow-md"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                            <button
                                onClick={openEditSectionModal}
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-medium rounded-lg hover:from-indigo-600 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit About Section
                            </button>

                            <button
                                onClick={handleDeleteSection}
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg hover:from-red-600 hover:to-red-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete Section
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="mb-6">
                    <p>No About Section found. Please create one.</p>
                    <button
                        onClick={openCreateSectionModal}
                        className="px-6 py-3 mt-3 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    >
                        Create About Section
                    </button>
                </div>
            )}

            {/* About Stats display */}
            <div className="mb-10">
                <h2 className="text-2xl font-semibold mb-4 flex items-center justify-between">
                    About Stats
                    <button
                        onClick={openCreateStatModal}
                        className="px-4 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    >
                        + Add Stat
                    </button>
                </h2>

                {loadingStats && <p>Loading stats...</p>}

                {!loadingStats && aboutStats.length === 0 && <p>No About Stats found.</p>}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    {aboutStats.map(stat => (
                        <div
                            key={stat.id}
                            className="p-6 rounded shadow text-center relative"
                            style={{
                                backgroundColor: stat.backgroundColor || '#ddd',
                                border: `2px solid ${stat.borderColor || '#aaa'}`,
                            }}
                        >
                            <p className="text-3xl font-bold text-indigo-700">{stat.number}</p>
                            <p className="capitalize text-indigo-900 mt-2">{stat.label}</p>

                            {/* Edit and Delete buttons */}
                            <div className="!absolute top-2 right-2 space-x-2">
                                <button
                                    onClick={() => openEditStatModal(stat)}
                                    title="Edit Stat"
                                    className="text-indigo-600 hover:text-indigo-900"
                                >
                                    ✏️
                                </button>
                                <button
                                    onClick={() => handleDeleteStat(stat.id)}
                                    title="Delete Stat"
                                    className="text-red-600 hover:text-red-900"
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal for About Section Create/Edit */}
            {showSectionModal && (
                <Modal isOpen={showSectionModal} onClose={() => setShowSectionModal(false)}>
                    <div className="p-6 max-w-lg">
                        <h3 className="text-xl font-semibold mb-4">
                            {sectionModalType === 'create' ? 'Create About Section' : 'Edit About Section'}
                        </h3>
                        <form onSubmit={handleSectionSubmit} className="space-y-4">
                            <div>
                                <label className="block mb-1 font-medium">Title *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={sectionFormData.title}
                                    onChange={handleSectionInputChange}
                                    required
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Main Description *</label>
                                <textarea
                                    name="mainDescription"
                                    value={sectionFormData.mainDescription}
                                    onChange={handleSectionInputChange}
                                    required
                                    rows={3}
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Detailed Description</label>
                                <textarea
                                    name="detailedDescription"
                                    value={sectionFormData.detailedDescription}
                                    onChange={handleSectionInputChange}
                                    rows={4}
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Video URL</label>
                                <input
                                    type="text"
                                    disabled={true} // Disable if you want to force file upload
                                    name="videoUrl"
                                    value={sectionFormData.videoUrl}
                                    onChange={handleSectionInputChange}
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
                                />
                                <input
                                    type="file"
                                    accept="video/*"
                                    onChange={(e) => handleFileSelect(e, 'videoUrl')}
                                    className="mb-2"
                                />
                                {uploadingFile && uploadField === 'videoUrl' && (
                                    <button
                                        type="button"
                                        onClick={handleUploadFile}
                                        disabled={uploadLoading}
                                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                    >
                                        {uploadLoading ? "Uploading..." : `Upload ${uploadingFile.name}`}
                                    </button>
                                )}
                            </div>
                            <div>
                                <label className="block mb-1 font-medium">Video Thumbnail URL</label>
                                <input
                                    type="text"
                                    name="videoThumbnail"
                                    disabled={true}
                                    value={sectionFormData.videoThumbnail}
                                    onChange={handleSectionInputChange}
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
                                />
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileSelect(e, 'videoThumbnail')}
                                    className="mb-2"
                                />
                                {uploadingFile && uploadField === 'videoThumbnail' && (
                                    <button
                                        type="button"
                                        onClick={handleUploadFile}
                                        disabled={uploadLoading}
                                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                    >
                                        {uploadLoading ? "Uploading..." : `Upload ${uploadingFile.name}`}
                                    </button>
                                )}
                            </div>



                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowSectionModal(false)}
                                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 bg-indigo-600 text-white rounded disabled:opacity-50 hover:bg-indigo-700"
                                >
                                    {loading ? (sectionModalType === 'create' ? 'Creating...' : 'Updating...') : (sectionModalType === 'create' ? 'Create' : 'Update')}
                                </button>
                            </div>
                        </form>
                    </div>
                </Modal>
            )}

            {/* Modal for About Stat Create/Edit */}
            {showStatModal && (
                <Modal isOpen={showStatModal} onClose={() => setShowStatModal(false)}>
                    <div className="p-6 max-w-md">
                        <h3 className="text-xl font-semibold mb-4">
                            {statModalType === 'create' ? 'Create About Stat' : 'Edit About Stat'}
                        </h3>
                        <form onSubmit={handleStatSubmit} className="space-y-4">
                            <div>
                                <label className="block mb-1 font-medium">Number *</label>
                                <input
                                    type="text"
                                    name="number"
                                    value={statFormData.number}
                                    onChange={handleStatInputChange}
                                    required
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g. 1000"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Label *</label>
                                <input
                                    type="text"
                                    name="label"
                                    value={statFormData.label}
                                    onChange={handleStatInputChange}
                                    required
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g. Students"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Background Color</label>
                                <input
                                    type="color"
                                    name="backgroundColor"
                                    value={statFormData.backgroundColor}
                                    onChange={handleStatInputChange}
                                    className="w-full h-10 p-0 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Border Color</label>
                                <input
                                    type="color"
                                    name="borderColor"
                                    value={statFormData.borderColor}
                                    onChange={handleStatInputChange}
                                    className="w-full h-10 p-0 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowStatModal(false)}
                                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loadingStats}
                                    className="px-6 py-2 bg-indigo-600 text-white rounded disabled:opacity-50 hover:bg-indigo-700"
                                >
                                    {loadingStats ? (statModalType === 'create' ? 'Creating...' : 'Updating...') : (statModalType === 'create' ? 'Create' : 'Update')}
                                </button>
                            </div>
                        </form>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default About;
